import { z } from 'zod';

import type { OrderCommunicationSubscription, OrderCommunicationSubscriptions } from '#domain/Subscription.ts';
import { apiNotificationsRoot } from '#store/apiNotificationsRoot.ts';

import { mapSubscriptions, mapSubscriptionsToApi, mapSubscriptionToApi } from './adapters/subscriptionAdapter.ts';
import { validateWarn } from './validateZodHelper.ts';

const subscriptionEventTypeSchema = z
    .enum(['tour-started', 'tour-finished', 'problem-reported', 'deviation-reported'])
    .catch('tour-started');
const subscriptionImportanceSchema = z.enum(['low', 'medium', 'high']);
const subscriptionSchema = z
    .object({
        tour_id: z.string().optional(),
        event_type: subscriptionEventTypeSchema,
        importance: subscriptionImportanceSchema.optional(),
    })
    .passthrough();
const subscriptionsSchema = z.object({ items: z.array(subscriptionSchema) }).passthrough();

export type ApiSubscription = z.infer<typeof subscriptionSchema>;
export type ApiSubscriptions = z.infer<typeof subscriptionsSchema>;

const url = '/subscriptions';
const TAG = 'SUBSCRIPTIONS';

export const notificationsApiSlice = apiNotificationsRoot.injectEndpoints({
    endpoints: build => ({
        getSubscriptions: build.query<OrderCommunicationSubscriptions, { tourId?: string } | undefined>({
            query: params => ({
                url: url,
                method: 'GET',
                params: params?.tourId ? { tour_id: params.tourId } : undefined,
            }),
            providesTags: [TAG],
            transformResponse: (response, meta, arg) =>
                mapSubscriptions(validateWarn(subscriptionsSchema)(response, meta, arg)),
        }),
        postSubscription: build.mutation<void, OrderCommunicationSubscriptions>({
            query: subscriptions => ({
                url: url,
                method: 'POST',
                body: JSON.stringify(mapSubscriptionsToApi(subscriptions)),
            }),
            invalidatesTags: [TAG],
        }),
        deleteSubscription: build.mutation<void, OrderCommunicationSubscription>({
            query: subscription => {
                const apiSubscription = mapSubscriptionToApi(subscription);
                return {
                    url: url,
                    method: 'DELETE',
                    params: {
                        tour_id: apiSubscription.tour_id,
                        event_type: apiSubscription.event_type,
                    },
                };
            },
            invalidatesTags: [TAG],
        }),
    }),
});

export const { useGetSubscriptionsQuery, usePostSubscriptionMutation, useDeleteSubscriptionMutation } =
    notificationsApiSlice;
